"use client"

// Discoverability signal that complements the Recompute button: tells the
// tenant their historical data is stale relative to the current rule set.
//
// Render rule lives in `lib/shopify/bundles/staleness.ts.isRecomputeStale`
// — single source of truth shared with any future consumer (e.g. a
// server-side warning surface). Inputs: `latestCompletedAt` from
// `useBackgroundJob` (NOT `latestTerminal` — a failed run did NOT update
// historical data, so failed terminals must NOT reset the staleness
// clock) and `bundleRulesChangedAt` from
// `shopify_integrations.bundle_rules_changed_at` (the server-side bump
// timestamp, durable across reloads and tabs).
//
// Click → opens the same recompute dialog as the button (DRY via
// controlled-open API on `RecomputeButton`).

import { AlertTriangle } from "lucide-react"

import { isRecomputeStale } from "@/lib/shopify/bundles/staleness"

export interface RecomputeStalenessPillProps {
  /** Latest successful recompute's `completed_at` ISO timestamp, or null. */
  latestCompletedAt: string | null
  /**
   * ISO timestamp of the latest rule edit (any
   * `bundle_definitions` / `product_family_rules` create / update /
   * remove / reorder) from `shopify_integrations.bundle_rules_changed_at`,
   * or null when no edit has bumped it yet.
   */
  bundleRulesChangedAt: string | null
  /** Click handler — typically opens the same dialog the trigger button uses. */
  onTrigger: () => void
}

export function RecomputeStalenessPill({
  latestCompletedAt,
  bundleRulesChangedAt,
  onTrigger,
}: RecomputeStalenessPillProps): JSX.Element | null {
  if (!isRecomputeStale(latestCompletedAt, bundleRulesChangedAt)) return null

  return (
    <button
      type="button"
      onClick={onTrigger}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
    >
      <AlertTriangle className="h-3 w-3" />
      <span>Rules changed since last recompute</span>
    </button>
  )
}
